import styled from '@emotion/styled'

import { CheckIcon } from '@arcade/web/icons/react'

import { Flex } from '../../layout/Flex'
import Text from '../../Text/Text'

const DropdownContainer = styled.div<{ maxDropdownHeightPx?: number }>`
  position: absolute;
  left: 0;
  right: 0;
  margin-top: ${({ theme }) => theme.spacing.xxs};
  z-index: 99999;
  background: ${({ theme }) => theme.arcadeColors.component.inputBackgroundDefault};
  border-radius: ${({ theme }) => theme.border.radius.medium};
  border: 1px solid ${({ theme }) => theme.arcadeColors.component.uiDividerBorder};
  overflow-x: hidden;
  overflow-y: auto;
  ${({ maxDropdownHeightPx, theme }) =>
    maxDropdownHeightPx !== undefined ? `max-height: ${theme.pxToRem(maxDropdownHeightPx)};` : ''}
`

const DropdownItem = styled.div<{ highlighted: boolean }>`
  padding: ${({ theme }) => theme.spacing.m};
  cursor: pointer;
  ${({ theme }) => theme.motion.standardTransition}
  background: ${({ theme, highlighted }) => {
    if (highlighted) return theme.arcadeColors.component.cellBackgroundHover
    return theme.arcadeColors.component.cellBackgroundDefault
  }};
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.s};

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.arcadeColors.component.uiDividerBorder};
  }
`

const ItemText = styled(Text)`
  white-space: nowrap;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
`

const LoadingOrNoResultsContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.m};
  background: ${({ theme }) => theme.arcadeColors.component.cellBackgroundDefault};
`

export interface ArcadeInputDropdownOptionsProps<T> {
  /** Required */
  items: T[]
  getItemKey: (item: T) => string | number
  itemToString: (item: T) => string
  highlightedIndex: number
  onItemClick: (item: T) => void
  onItemMouseEnter: (index: number) => void
  /** Optional */
  ariaLabel?: string
  maxDropdownHeightPx?: number
  isLoading?: boolean
  loadingText?: string
  noResultsText?: string
  selectedValue?: T | string | null
  testId?: string
  listboxId?: string
}

export function ArcadeInputDropdownOptions<T>({
  items,
  getItemKey,
  itemToString,
  maxDropdownHeightPx,
  highlightedIndex,
  ariaLabel = 'Dropdown options',
  isLoading = false,
  loadingText,
  noResultsText,
  selectedValue,
  testId,
  listboxId,
  onItemClick,
  onItemMouseEnter,
}: ArcadeInputDropdownOptionsProps<T>) {
  const showLoading = isLoading
  const showNoResults = !isLoading && items.length === 0 && !!noResultsText
  const showResults = !isLoading && items.length > 0

  return (
    <DropdownContainer
      maxDropdownHeightPx={maxDropdownHeightPx}
      data-testid={testId ? `${testId}-dropdown` : undefined}
    >
      <div id={listboxId} role="listbox" aria-label={ariaLabel} data-testid={testId ? `${testId}-list` : undefined}>
        {(showLoading || showNoResults) && (
          <LoadingOrNoResultsContainer>
            <ItemText typography="input" color="textStandard" data-testid="loading-or-no-results-text">
              {showLoading ? loadingText : noResultsText}
            </ItemText>
          </LoadingOrNoResultsContainer>
        )}
        {showResults &&
          items.map((item, index) => {
            // check of selectedValue is user-inputted string or a selected option
            const hasInputValue = typeof selectedValue === 'string'
            const isSelected = !!(!hasInputValue && selectedValue && getItemKey(selectedValue) === getItemKey(item))
            return (
              <DropdownItem
                key={getItemKey(item)}
                data-index={index}
                role="option"
                id={`arcade-dropdown-option-${index}`}
                aria-selected={isSelected}
                highlighted={highlightedIndex === index}
                onMouseDown={e => {
                  e.preventDefault()
                  onItemClick(item)
                }}
                onMouseEnter={() => onItemMouseEnter(index)}
                data-testid={testId ? `${testId}-option-${index}` : undefined}
              >
                <ItemText typography="input" color="textStandard">
                  {itemToString(item)}
                </ItemText>
                {isSelected && (
                  <Flex alignItems="center" flexShrink={0}>
                    <Text color="textStandard">
                      <CheckIcon size={16} />
                    </Text>
                  </Flex>
                )}
              </DropdownItem>
            )
          })}
      </div>
    </DropdownContainer>
  )
}

export default ArcadeInputDropdownOptions
